import localStorageHelper from '@/helper/localStorageHelper';
import { $settings } from '@/provider/settings';
import { isInOffice } from '@/utils/isInOffice';
import { isInTeams } from '@/utils/isInTeams';
import * as msal from '@azure/msal-browser';
import * as microsoftTeams from '@microsoft/teams-js';
import axios from 'axios';
import { jwtDecode } from 'jwt-decode';
import { useAuthStore } from '@/store/auth';

class AuthService {
  constructor() {
    this.profile = localStorageHelper.getProfile();
    this.axiosInstance = axios.create();
    const { clientId, authority, redirectUri, postLogoutRedirectUri } =
      $settings.authentication;

    this.msalConfig = {
      auth: {
        clientId,
        authority,
        redirectUri,
        postLogoutRedirectUri,
        navigateToLoginRequestUrl: false,
      },
      cache: {
        cacheLocation: 'localStorage',
        storeAuthStateInCookie: true,
      },
      system: {
        asyncPopups: true,
        allowRedirectInIframe: true,
        /*
           *** DEBUGGING OPTIONS FOR MSAL ***
           *** DONT USE IN PRODUCTION ***
          loggerOptions: {
            loggerCallback: (level, message, containsPii) => {
              if (containsPii) {
                return;
              }
              switch (level) {
                case 0:
                  console.error(message);
                  return;
                case 1:
                  console.warn(message);
                  return;
                case 2:
                  console.info(message);
                  return;
                case 3:
                  console.debug(message);
                  return;
                case 4:
                  console.debug(message);
              }
            },
            logLevel: 3,
            piiLoggingEnabled: false,
          },
           */
        windowHashTimeout: 30000,
        iframeHashTimeout: 30000,
        loadFrameTimeout: 30000,
      },
    };

    // init MSAL
    this.initMSAL();

    // Teams Handling
    this.initForTeams();
  }

  /*-------------------------*/
  /* GENERAL PURPOSE METHODS */
  /*-------------------------*/

  async initMSAL() {
    this.msalInstance = new msal.PublicClientApplication(this.msalConfig);
    await this.msalInstance.initialize();
  }

  async login() {
    if (await this.isAuthenticated()) {
      return;
    }

    if (!isInTeams()) {
      await this.msalInstance.loginPopup().then((response) => {
        this.selectAccount(response);
      });
    }
  }

  async logout() {
    if (!isInTeams()) {
      await this.msalInstance
        .logoutPopup({
          account: this.msalInstance.getAccountByHomeId(this.accountId),
          postLogoutRedirectUri: this.msalConfig.auth.postLogoutRedirectUri,
          mainWindowRedirectUri: this.msalConfig.auth.postLogoutRedirectUri,
        })
        .then(() => {
          console.info('Logout successful');
        })
        .catch((error) => {
          console.error('Logout failed:', error);
        });
    }
  }

  async isAuthenticated() {
    const token = await this.getToken();
    if (!token) {
      return false;
    }

    const profile = localStorageHelper.getProfile();
    if (!profile) {
      return false;
    }
    const expiryTime =
      token.extExpiresOn || localStorage.getItem('teams-token-expiry');

    return Date.now() <= new Date(expiryTime).getTime();
  }

  selectAccount(response = null, token = null) {
    if (response !== null) {
      this.profile = {
        ...this.profile,
        username: response.account.username,
        accountId: response.account.homeAccountId,
      };
      token = response.idToken;
    }
    const currentAccounts = this.msalInstance.getAllAccounts();
    if (isInTeams() || isInOffice()) {
      const decodedToken = token && jwtDecode(token);
      const { oid, tid } = decodedToken;
      decodedToken.accountId = `${oid}.${tid}`;
      this.profile = { ...this.profile, ...decodedToken };
      useAuthStore().setToken(token);
    } else {
      if (currentAccounts.length === 0) {
        console.warn('No accounts detected.');
      }
      if (currentAccounts.length > 1) {
        console.warn('Multiple accounts detected.');
      } else if (currentAccounts.length === 1) {
        this.profile = {
          ...this.profile,
          accountId: currentAccounts[0].homeAccountId,
          username: currentAccounts[0].username,
          fullName: currentAccounts[0].name,
        };
      }
    }
    useAuthStore().setProfile(this.profile);
    localStorageHelper.setProfile(this.profile);
  }

  getProfile() {
    return this.profile;
  }

  async getToken() {
    let token;
    if (isInTeams()) {
      token = await this.getTeamsCachedToken();
    } else if (isInOffice()) {
      token = this.getOfficeToken([
        `${$settings.authentication.audience}/webX`,
      ]);
    } else {
      token = this.getAccessTokenByScopes([
        `${$settings.authentication.audience}/webX`,
      ]);
    }
    return token;
  }

  async getAccessTokenByScopes(scopes) {
    const account = this.msalInstance.getAccountByHomeId(
      this.profile.accountId,
    );
    return this.msalInstance
      .acquireTokenSilent({
        scopes,
        account,
      })
      .then((response) => response)
      .catch((silentError) => {
        // call acquireTokenPopup in case of acquireTokenSilent failure
        // due to consent or interaction required
        if (
          silentError.errorCode === 'consent_required' ||
          silentError.errorCode === 'interaction_required' ||
          silentError.errorCode === 'login_required'
        ) {
          this.msalInstance
            .acquireTokenPopup(scopes)
            .then((response) => response)
            .catch((popupError) => {
              console.error(popupError);
            });
        }
      });
  }

  /*----------------*/
  /* OFFICE METHODS */
  /*----------------*/

  async getOfficeToken(scopeParameter) {
    try {
      const myAccounts = this.msalInstance.getAllAccounts();
      if (myAccounts?.length > 0) {
        const firstAccount = myAccounts[0];
        const tokenAuthority = await this.msalInstance.acquireTokenSilent({
          scopes: scopeParameter,
          account: firstAccount,
        });
        return tokenAuthority;
      }
    } catch (error) {
      console.error(error);
    }
    return null;
  }

  /*---------------*/
  /* GRAPH METHODS */
  /*---------------*/

  async getGraphToken() {
    if (isInTeams()) {
      return this.getTeamsCachedToken();
    }
    const accessTokenRW = await this.getAccessTokenByScopes([
      'Calendars.ReadWrite',
    ]);
    const accessTokenR = await this.getAccessTokenByScopes(['Calendars.Read']);
    return accessTokenRW || accessTokenR;
  }

  /*---------------*/
  /* TEAMS METHODS */
  /*---------------*/

  async initForTeams() {
    if (isInTeams()) {
      await microsoftTeams.initialize();
      microsoftTeams.authentication.getAuthToken({
        resources: [`${$settings.authentication.teamsScope}`],
        silent: false,
        successCallback: (token) => {
          this.selectAccount(null, token);
        },
        failureCallback: (error) => {
          console.error(error);
        },
      });
    }
  }

  async getTeamsCachedToken() {
    let accessToken = null;
    const currentDate = new Date();
    const expiryString = localStorage.getItem('teams-token-expiry');
    if (!expiryString) {
      accessToken = await this.getTeamsAccessToken();
    }
    const expiryDate = new Date(expiryString);
    if (currentDate.getTime() > expiryDate.getTime()) {
      try {
        accessToken = await this.refreshTeamsToken();
      } catch {
        accessToken = await this.getTeamsAccessToken();
      }
    } else {
      accessToken = {
        accessToken: localStorage.getItem('teams-token'),
      };
    }
    return accessToken;
  }

  async getTeamsAccessToken() {
    const token = localStorageHelper.getToken();
    // eslint-disable-next-line security/detect-possible-timing-attacks
    if (token !== '') {
      const accessTokenResponse = await this.axiosInstance.get(
        `${$settings.middlewareURI}/token/exchange?scopes=${encodeURI(`${$settings.authentication.audience}/webX`)}`,
        {
          headers: {
            authorization: `Bearer ${token}`,
          },
        },
      );
      AuthService.cacheTeamsToken(accessTokenResponse.data);
      return accessTokenResponse.data;
    }
    return false;
  }

  async refreshTeamsToken() {
    const refreshResponse = await this.axiosInstance.get(
      `${$settings.middlewareURI}/token/refresh`,
      {
        headers: {
          authorization: `Bearer ${localStorage.getItem('teams-refresh-token')}`,
        },
      },
    );
    AuthService.cacheTeamsToken(refreshResponse.data);
    return refreshResponse.data;
  }

  static cacheTeamsToken(tokenResponse) {
    const expiry = new Date();
    expiry.setSeconds(expiry.getSeconds() + tokenResponse.expiresIn - 300);
    localStorage.setItem('teams-token', tokenResponse.accessToken);
    localStorage.setItem('teams-token-expiry', expiry.toISOString());
    localStorage.setItem('teams-refresh-token', tokenResponse.refreshToken);
  }
}

const authService = new AuthService();
export { authService };
